import React, { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Link, Stack, TextField, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ContactChatLaunchProvider, personalEmail, useContactChatLaunch } from '../contact-chat/ContactChatLaunch';
import * as api from '../api/raTrainingFunctions';
import type { RaContact, RaContacts, RaContactReportInput, RaContent, RaScenario } from './types';

const validEmail = (value?: string | null) => Boolean(value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value));
const hasContact = (entry?: RaContact | null) => Boolean(entry && (entry.person_name?.trim() || entry.office_name?.trim() || validEmail(entry.email) || entry.phones?.some(phone => phone.number.trim())));

export type RaContactReportContext = { kind: 'scenario' | 'contact_guide' | 'question' | 'directory'; id?: string };

const Context = createContext<{ data: RaContacts | null; report: (role?: string, context?: RaContactReportContext) => void; refresh: () => Promise<void>; loading: boolean; failed: boolean; saved: boolean }>({ data: null, report: () => undefined, refresh: async () => undefined, loading: false, failed: false, saved: false });
const assignmentRoles = new Set(['requirement_owner', 'funds_certifier', 'assigned_cardholder', 'approving_official', 'assigned_contracting_officer', 'designated_representative']);
// Reusable unit contacts. Assigned officials remain available beside the
// situation where they are needed, rather than as a single unit-wide person.
const guideRoles = new Set(['contracting_intake', 'flight_chief', 'budget_office', 'gpc_program_office', 'facilities_office']);
export const isVisibleTrainingContactRole = (role: string) => role !== 'requirement_owner' && role !== 'unit_leadership';
export const raContactRoleLabels: Record<string, string> = {
  budget_office: 'Supporting financial management/budget office', contracting_intake: 'Supporting contracting office',
  flight_chief: 'Contracting flight chief',
  gpc_program_office: 'Local Agency/Organization Program Coordinator', facilities_office: 'Supporting Civil Engineer/facilities office',
  ra_lead: 'Resource advisor lead', funds_certifier: 'Authorized funds certifier', unit_leadership: 'Unit leadership',
  cost_center_manager: 'Cost-center manager', requirement_owner: 'Requirement owner', assigned_cardholder: 'Assigned cardholder',
  approving_official: 'Approving/billing official', assigned_contracting_officer: 'Assigned contracting officer',
  designated_representative: 'Designated contracting officer’s representative, if appointed',
};

// Local contact roles vary by unit. These descriptions explain the role, not
// an individual's appointment or authority to approve a particular action.
const roleGuidance: Record<string, { does: string; when: string }> = {
  contracting_intake: { does: 'The contracting team handles purchase requests and contracts for your unit.', when: 'Ask where to send a new request, what goes in the package, or who manages your contract.' },
  flight_chief: { does: 'Leads a local contracting team and coordinates its work.', when: 'If your unit lists one, ask for help finding the right team or following up on a request.' },
  budget_office: { does: 'The financial management team helps your unit plan and track its funding.', when: 'Ask whether funds are available, which funding citation to use, and who can certify them.' },
  gpc_program_office: { does: 'The Government Purchase Card (GPC) program coordinator helps cardholders follow local rules.', when: 'Ask who can make a card purchase and what local paperwork is required.' },
  facilities_office: { does: 'The Civil Engineer team handles requests for work on buildings and facilities.', when: 'Ask how to submit a repair or construction request and who will review it.' },
  ra_lead: { does: 'Helps resource advisors follow local budget and reporting procedures.', when: 'Ask for help with resource advisor duties or local procedures.' },
  funds_certifier: { does: 'An authorized official who certifies that funds are available for a purchase.', when: 'Ask your budget office to route the request to the person authorized to certify it.' },
  unit_leadership: { does: 'Sets unit priorities and makes decisions assigned under local procedures.', when: 'Contact for priority decisions or escalation as directed locally.' },
  cost_center_manager: { does: 'Manages the spending plan for an assigned cost center.', when: 'Ask about a change to that plan or how to report a shortfall.' },
  requirement_owner: { does: 'Defines the mission need for a particular requirement.', when: 'Work with the owner to describe the need and review the package.' },
  assigned_cardholder: { does: 'Makes Government Purchase Card purchases within their authority and limits.', when: 'Ask the assigned cardholder for their checklist and send them the reviewed package.' },
  approving_official: { does: 'Reviews the assigned cardholder’s purchases and billing.', when: 'Ask which approvals and records are required for the purchase.' },
  assigned_contracting_officer: { does: 'Awards and manages contracts within their delegated authority.', when: 'Contact the assigned officer about contract changes or other formal contract decisions.' },
  designated_representative: { does: 'If appointed, monitors contractor performance for the contracting officer; they cannot change the contract.', when: 'Report performance or delivery problems to the appointed representative, or to the contracting officer if none is appointed.' },
};

export function RaRoleInfo({ roleId, label }: { roleId: string; label?: string }) {
  const [open, setOpen] = useState(false);
  const titleId = useId();
  const title = label || raContactRoleLabels[roleId] || roleId.replace(/_/g, ' ');
  const guidance = roleGuidance[roleId] || {
    does: 'This role supports your local purchase process.',
    when: 'Ask your supporting contracting office who can help with your request.',
  };
  return <>
    <IconButton size="small" color="primary" aria-label={`About ${title}`} onClick={() => setOpen(true)} sx={{ p: 0.5, flexShrink: 0 }}>
      <InfoOutlinedIcon fontSize="small" />
    </IconButton>
    <Dialog open={open} onClose={() => setOpen(false)} aria-labelledby={titleId} fullWidth maxWidth="sm">
      <DialogTitle id={titleId}>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2">What they do</Typography>
        <Typography sx={{ mb: 2 }}>{guidance.does}</Typography>
        <Typography variant="subtitle2">When to contact</Typography>
        <Typography>{guidance.when}</Typography>
      </DialogContent>
      <DialogActions><Button onClick={() => setOpen(false)}>Close</Button></DialogActions>
    </Dialog>
  </>;
}

export function RaContactsProvider({ children, scopeKey, projectedData }: { children: React.ReactNode; scopeKey: string; projectedData?: RaContacts | null }) {
  const [data, setData] = useState<RaContacts | null>(projectedData || null);
  const [dataScope, setDataScope] = useState(scopeKey);
  const scope = useRef(scopeKey);
  scope.current = scopeKey;
  const visibleData = dataScope === scopeKey ? data : null;
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<RaContactReportInput | null>(null);
  const [explanation, setExplanation] = useState('');
  const [reportState, setReportState] = useState<'idle' | 'saving' | 'error' | 'saved'>('idle');
  const epoch = useRef(0);
  const inFlight = useRef(false);
  const alive = useRef(true);
  const refresh = useCallback(async () => {
    if (projectedData !== undefined || inFlight.current) return;
    const requestEpoch = epoch.current;
    const requestScope = scope.current;
    inFlight.current = true;
    // Keep the current details mounted while revalidating the same account.
    // A real account/role boundary is cleared by the scope effect below.
    setLoading(true);
    setFailed(false);
    try {
      const next = await api.getRaContacts();
      if (alive.current && requestEpoch === epoch.current && requestScope === scope.current) { setData(next); setDataScope(requestScope); }
    } catch {
      if (alive.current && requestEpoch === epoch.current) setFailed(true);
    } finally {
      if (alive.current && requestEpoch === epoch.current) { inFlight.current = false; setLoading(false); }
    }
  }, [projectedData]);
  useLayoutEffect(() => {
    alive.current = true;
    const activeEpoch = epoch.current + 1;
    epoch.current = activeEpoch;
    inFlight.current = false;
    if (projectedData !== undefined) {
      setData(projectedData); setDataScope(scopeKey); setReport(null); setReportState('idle');
      return () => { alive.current = false; epoch.current = activeEpoch + 1; };
    }
    setData(null);
    setReport(null);
    setReportState('idle');
    void refresh();
    const focus = () => void refresh();
    const visible = () => { if (document.visibilityState === 'visible') void refresh(); };
    window.addEventListener('focus', focus);
    document.addEventListener('visibilitychange', visible);
    return () => { alive.current = false; epoch.current = activeEpoch + 1; inFlight.current = false; window.removeEventListener('focus', focus); document.removeEventListener('visibilitychange', visible); };
  }, [refresh, scopeKey, projectedData]);
  // Re-entering instructions reloads stored contacts, independently of lesson state.
  const openReport = (role?: string, context?: RaContactReportContext) => {
    const entry = role ? visibleData?.contacts[role] : null;
    setReport({ idempotency_key: globalThis.crypto?.randomUUID?.() || `report-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      context_kind: context?.kind || 'directory', context_id: context?.id, role_id: role || null, contact_id: hasContact(entry) ? entry!.id : null, displayed_revision: entry?.revision || visibleData?.directory_revision || null });
    setExplanation('');
    setReportState('idle');
  };
  const send = async () => {
    if (!report || reportState === 'saving') return;
    const requestEpoch = epoch.current;
    setReportState('saving');
    try {
      await api.reportRaContact({ ...report, explanation: explanation.trim() });
      if (alive.current && epoch.current === requestEpoch) { setReport(null); setReportState('saved'); }
    } catch { if (alive.current && epoch.current === requestEpoch) setReportState('error'); }
  };
  return <Context.Provider value={{ data: visibleData, report: openReport, refresh, loading, failed, saved: reportState === 'saved' }}>
    <ContactChatLaunchProvider key={scopeKey} data={visibleData} scopeKey={scopeKey}><InstructionRefreshContext.Provider value={refresh}>{children}</InstructionRefreshContext.Provider></ContactChatLaunchProvider>
    <Dialog open={Boolean(report)} onClose={() => reportState !== 'saving' && setReport(null)} fullWidth maxWidth="sm">
      <DialogTitle>{report?.contact_id ? 'Report incorrect contact' : 'Provide contact'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>This creates a feedback item for the Waypoint maintainers, including your unit and where this contact belongs.</Typography>
        {report?.context_kind === 'directory' && <TextField select fullWidth label="Contact role" value={report.role_id || ''}
          SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} sx={{ mb: 2 }}
          disabled={reportState === 'saving' || reportState === 'error'} onChange={event => setReport(previous => previous ? { ...previous, role_id: event.target.value || null } : previous)}>
          <option value="">Choose the contact's role</option>
          {Object.entries(raContactRoleLabels).filter(([id]) => guideRoles.has(id)).map(([id, title]) => <option key={id} value={id}>{title}</option>)}
        </TextField>}
        <Typography variant="body2" sx={{ mb: 2 }}>{report?.role_id ? `Contact role: ${raContactRoleLabels[report.role_id] || report.role_id.replace(/_/g, ' ')}` : 'Share a contact for your unit.'}</Typography>
        <TextField autoFocus fullWidth multiline minRows={2} value={explanation}
          inputProps={{ maxLength: 1000, 'aria-label': report?.contact_id ? 'Correction details' : 'Contact details' }} onChange={event => setExplanation(event.target.value)} disabled={reportState === 'saving' || reportState === 'error'} />
        {reportState === 'error' && <Alert severity="error" sx={{ mt: 2 }}>Report could not be saved. Please retry.</Alert>}
      </DialogContent>
      <DialogActions><Button disabled={reportState === 'saving'} onClick={() => setReport(null)}>Cancel</Button>
        <Button variant="contained" disabled={reportState === 'saving' || !report?.role_id} onClick={() => void send()}>{report?.contact_id ? 'Send report' : 'Send contact'}</Button></DialogActions>
    </Dialog>
  </Context.Provider>;
}
export function RaContactsToolbar() {
  const { data, failed, saved } = useContext(Context);
  return <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
    {failed && <Typography variant="body2" color="text.secondary">{data ? 'Could not refresh contacts. Showing last loaded contacts.' : 'Local contacts unavailable. Use the role guidance and retry.'}</Typography>}
    {saved && <Typography role="status" variant="body2">Report saved.</Typography>}
  </Stack>;
}
const InstructionRefreshContext = createContext<() => Promise<void>>(async () => undefined);
export function useInstructionContactsRefresh() { return useContext(InstructionRefreshContext); }

export function RaContactGuideHeading({ printOnly = false }: { printOnly?: boolean } = {}) {
  const { data, report } = useContext(Context);
  return <Box sx={{ mb: 1 }}>
    {data?.assigned_unit && <Typography fontWeight={600}>Contacts for {data.assigned_unit}</Typography>}
    {data && !data.unit_id && !Object.entries(data.contacts).some(([role, entry]) => guideRoles.has(role) && hasContact(entry)) && !data.conflicted_roles?.some(role => guideRoles.has(role)) && <Typography variant="body2" color="text.secondary">Local contacts are not configured for your unit.</Typography>}
    {!printOnly && !Object.entries(data?.contacts || {}).some(([role, entry]) => guideRoles.has(role) && hasContact(entry)) && <Button size="small" onClick={() => report()}>Provide contact</Button>}
  </Box>;
}

type GuideRole = { role: string; contextId?: string };
type ContactGroup<Role> = { key: string; contact?: RaContact; roles: Role[] };
const normalized = (value?: string | null) => value?.trim().toLocaleLowerCase() || '';
function sameContact(left: RaContact, right: RaContact) {
  if (left.id && left.id === right.id) return true;
  // An office mailbox or phone can serve several different people. Only an
  // individual address with a compatible name can join separate records.
  return personalEmail(left) && personalEmail(right)
    && normalized(left.email) === normalized(right.email)
    && normalized(left.person_name) === normalized(right.person_name);
}

function groupContactRoles<Role extends { role: string }>(roles: Iterable<Role>, data: RaContacts | null): ContactGroup<Role>[] {
  const groups: ContactGroup<Role>[] = [];
  Array.from(roles).forEach(role => {
    const candidate = data?.contacts[role.role];
    const contact = hasContact(candidate) ? candidate : undefined;
    const existing = contact && groups.find(group => group.contact && sameContact(group.contact, contact));
    if (existing) existing.roles.push(role);
    else groups.push({ key: contact?.id || role.role, contact, roles: [role] });
  });
  return groups;
}

/** A role belongs to an authored instruction; directory details stay current and local. */
export function RaStepContacts({ roleIds, context, printOnly = false, onProvideContact }: {
  roleIds: readonly string[];
  context: RaContactReportContext;
  printOnly?: boolean;
  /** Uses general feedback when the caller has no private directory entitlement. */
  onProvideContact?: (roleId: string) => void;
}) {
  const { data, report, loading } = useContext(Context);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const titleId = useId();
  const roles = Array.from(new Set(roleIds)).filter(role => role && isVisibleTrainingContactRole(role));
  const selectedAvailable = selectedRole && roles.includes(selectedRole) && hasContact(data?.contacts[selectedRole]);
  useEffect(() => {
    if (selectedRole && !selectedAvailable) setSelectedRole(null);
  }, [selectedAvailable, selectedRole]);
  if (!roles.length) return null;
  if (printOnly) return <Stack spacing={0.75} sx={{ mt: 0.75 }}>
    {roles.map(role => <Box key={role}>
      <Typography variant="body2" fontWeight={600}>{raContactRoleLabels[role] || role.replace(/_/g, ' ')}</Typography>
      {hasContact(data?.contacts[role])
        ? <RaContactDetails roleId={role} context={context} printOnly compact roleTitle={raContactRoleLabels[role]} />
        : <Typography variant="body2">no contact available</Typography>}
    </Box>)}
  </Stack>;
  return <>
    <Stack spacing={0.25} sx={{ mt: 0.5 }}>
      {roles.map(role => {
        const label = raContactRoleLabels[role] || role.replace(/_/g, ' ');
        const available = hasContact(data?.contacts[role]);
        return <Stack key={role} direction="row" alignItems="baseline" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography component="span" variant="body2" color="text.primary">{label}</Typography>
          <RaRoleInfo roleId={role} />
          {!available && data?.conflicted_roles?.includes(role) && <Typography component="span" variant="body2">Multiple contacts need resolution.</Typography>}
          <Button size="small" sx={{ px: 0, minHeight: 32 }} disabled={loading && !data}
            aria-label={`${available ? 'Contact info' : 'Provide contact'} for ${label}`}
            onClick={() => available ? setSelectedRole(role) : onProvideContact ? onProvideContact(role) : report(role, context)}>
            {available ? 'Contact info' : 'Provide contact'}
          </Button>
        </Stack>;
      })}
    </Stack>
    <Dialog open={Boolean(selectedAvailable)} onClose={() => setSelectedRole(null)} aria-labelledby={titleId} fullWidth maxWidth="sm">
      <DialogTitle id={titleId}>{selectedRole ? raContactRoleLabels[selectedRole] || selectedRole.replace(/_/g, ' ') : 'Contact info'}</DialogTitle>
      <DialogContent>
        {selectedAvailable && <RaContactDetails roleId={selectedRole!} context={context} compact roleTitle={raContactRoleLabels[selectedRole!]} />}
      </DialogContent>
      <DialogActions><Button onClick={() => setSelectedRole(null)}>Close</Button></DialogActions>
    </Dialog>
  </>;
}

type ScenarioRole = { role: string; primary: boolean };
function ScenarioContactGroup({ group, scenarioId, printOnly = false }: { group: ContactGroup<ScenarioRole>; scenarioId: string; printOnly?: boolean }) {
  const [selected, setSelected] = useState(group.roles[0].role);
  const current = group.roles.find(role => role.role === selected) || group.roles[0];
  return <Box sx={{ minWidth: 0 }}>
    {group.roles.map(role => <Typography key={role.role} variant="body2" fontWeight={role.primary ? 700 : 500}>
      {!role.primary && 'Supporting contact: '}{raContactRoleLabels[role.role] || role.role.replace(/_/g, ' ')}
      {!printOnly && <RaRoleInfo roleId={role.role} />}
    </Typography>)}
    {!printOnly && group.roles.length > 1 && <TextField select fullWidth size="small" label="Contact role" value={current.role}
      SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} sx={{ mt: 1.5 }}
      onChange={event => setSelected(event.target.value)}>
      {group.roles.map(role => <option key={role.role} value={role.role}>{raContactRoleLabels[role.role] || role.role.replace(/_/g, ' ')}</option>)}
    </TextField>}
    <RaContactDetails printOnly={printOnly} roleId={current.role} context={{ kind: 'scenario', id: scenarioId }} />
  </Box>;
}

/** Keep relevant roles nearby without repeating a person who supports several. */
export function RaScenarioContacts({ scenario, printOnly = false }: { scenario: RaScenario; printOnly?: boolean }) {
  const { data } = useContext(Context);
  const roles = Array.from(new Set([scenario.primary_role_id, ...(scenario.supporting_role_ids || [])]))
    .filter((role): role is string => Boolean(role) && isVisibleTrainingContactRole(role!))
    .map(role => ({ role, primary: role === scenario.primary_role_id }));
  const groups = groupContactRoles(roles, data);
  const headingId = `ra-scenario-contacts-${scenario.id}${printOnly ? '-print' : ''}`;
  return <Box component="section" aria-labelledby={headingId}>
    <Typography component="h3" variant="body1" fontWeight={700} id={headingId} sx={{ mb: 2 }}>Contacts for this situation</Typography>
    <Stack spacing={2} sx={{ '& > * + *': { borderTop: 1, borderColor: 'divider', pt: 2 } }}>
      {groups.map(group => <ScenarioContactGroup key={group.key} group={group} scenarioId={scenario.id} printOnly={printOnly} />)}
    </Stack>
    {!roles.length && <Typography>{scenario.contacts.join('; ')}</Typography>}
  </Box>;
}

function ContactGuideEntry({ role, contact, printOnly = false }: { role: GuideRole; contact?: RaContact; printOnly?: boolean }) {
  const identity = contact?.person_name || contact?.office_name;
  return <Box sx={contactGuideCardSx}>
    {identity && <Typography component="h3" sx={{ fontSize: 17, lineHeight: 1.5, fontWeight: 700, overflowWrap: 'anywhere' }}>{identity}</Typography>}
    <Stack spacing={0.25} sx={{ mt: identity ? 0.25 : 0 }}>
      <Typography component={identity ? 'p' : 'h3'} variant="body1"
        aria-label={!identity ? raContactRoleLabels[role.role] || role.role.replace(/_/g, ' ') : undefined}
        sx={{ fontWeight: 600, color: 'text.primary', display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <Box component="span" sx={{ minWidth: 0 }}>{raContactRoleLabels[role.role] || role.role.replace(/_/g, ' ')}</Box>
        {!printOnly && <RaRoleInfo roleId={role.role} />}
      </Typography>
    </Stack>
    {!printOnly && assignmentRoles.has(role.role) && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
      Confirm the person assigned to your requirement or purchase.
    </Typography>}
    <RaContactDetails printOnly={printOnly} roleId={role.role} context={{ kind: 'contact_guide', id: role.contextId }} guideIdentity={Boolean(identity)} />
  </Box>;
}

const contactGuideCardSx = {
  p: 1.5, bgcolor: 'background.paper', color: 'text.primary',
  border: 1, borderColor: 'divider', borderRadius: 1, minWidth: 0,
};

/** Show a separate entry for each reusable unit contact role. */
export function RaContactGuide({ guide, printOnly = false, headerAction }: { guide: RaContent['contact_guide']; printOnly?: boolean; headerAction?: React.ReactNode }) {
  const { data } = useContext(Context);
  const roles = new Map<string, GuideRole>();
  const add = (role: string | undefined, contextId?: string) => {
    if (!role || !guideRoles.has(role)) return;
    if (!roles.has(role)) roles.set(role, { role, contextId });
    else if (contextId && !roles.get(role)?.contextId) roles.set(role, { role, contextId });
  };
  // Start with contracting, then show the other reusable unit offices.
  guideRoles.forEach(role => add(role));
  guide.forEach(item => {
    add(item.primary_role_id, item.id);
    item.supporting_contacts?.forEach(support => add(support.role_id, item.id));
  });
  Object.keys(data?.contacts || {}).forEach(role => add(role));
  data?.conflicted_roles?.forEach(role => add(role));
  const headingId = `ra-training/contacts${printOnly ? '-print' : ''}`;
  return <Box component="section" aria-labelledby={headingId}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
      <Typography component="h2" variant="h5" id={headingId}>Contact guide</Typography>
      {!printOnly && headerAction}
    </Box>
    <RaContactGuideHeading printOnly={printOnly} />
    {!printOnly && <Typography variant="body2" sx={{ mb: 1 }}>Start with your contracting office or local flight chief.</Typography>}
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
      {Array.from(roles.values(), role => {
        const candidate = data?.contacts[role.role];
        return <ContactGuideEntry key={role.role} role={role} contact={hasContact(candidate) ? candidate : undefined} printOnly={printOnly} />;
      })}
    </Box>
  </Box>;
}
export function RaLocalContactFallback({ roleId, text }: { roleId?: string; text: string }) {
  const { data } = useContext(Context);
  return roleId && hasContact(data?.contacts[roleId]) ? null : <Typography variant="body2" sx={{ mt: 0.75 }}>{text}</Typography>;
}
function safePage(value?: string | null) {
  try { const url = new URL(value || ''); return url.protocol === 'https:' && !url.username && !url.password && (!url.port || url.port === '443') ? value : null; }
  catch { return null; }
}
export function RaContactDetails({ roleId, context, printOnly = false, guideIdentity = false, compact = false, roleTitle }: { roleId?: string; context?: RaContactReportContext; printOnly?: boolean; guideIdentity?: boolean; compact?: boolean; roleTitle?: string }) {
  const { data, report } = useContext(Context);
  const launchChat = useContactChatLaunch();
  if (!roleId || roleId === 'requirement_owner') return null;
  const candidate = data?.contacts[roleId];
  const entry = hasContact(candidate) ? candidate : undefined;
  const intake = entry?.kind === 'intake' || assignmentRoles.has(roleId);
  const email = entry?.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(entry.email) ? entry.email : null;
  const page = safePage(entry?.contact_page);
  const guide = context?.kind === 'contact_guide';
  // Older guides may not have a placement ID. The directory context is the
  // supported fallback for both reports and add-email suggestions.
  const reportContext: RaContactReportContext | undefined = guide && !context?.id ? { kind: 'directory' } : context;
  const compactIdentity = entry?.person_name || (entry?.office_name?.trim().toLocaleLowerCase() !== roleTitle?.trim().toLocaleLowerCase() ? entry?.office_name : null);
  const officeContact = Boolean(entry && !entry.person_name?.trim());
  return <Stack spacing={compact ? 0.5 : 0.4} sx={{ mt: compact || guide ? 0.5 : 1, overflowWrap: 'anywhere' }}>
    {printOnly && !entry && <Typography variant="body2">no contact available</Typography>}
    {!printOnly && !entry && data?.conflicted_roles?.includes(roleId) && <Typography variant="body2">Multiple contacts need resolution.</Typography>}
    {!printOnly && compact && !entry && <Typography variant="body2" color="text.secondary">Contact not listed.</Typography>}
    {entry && compact ? <>
      {compactIdentity && <Typography variant="body2" fontWeight={600}>{compactIdentity}</Typography>}
      {email && <Link variant="body2" href={`mailto:${encodeURIComponent(email)}`}>{email}</Link>}
      {entry.phones?.map(phone => <Typography variant="body2" key={`${phone.kind}/${phone.number}`}>{/^[+\d\s().-]+$/.test(phone.number) ? <Link href={`tel:${phone.number.replace(/[^+\d]/g, '')}`}>{phone.number}</Link> : phone.number}</Typography>)}
      {page && <Link variant="body2" href={page} target="_blank" rel="noreferrer">Contact office page</Link>}
      {(officeContact || intake) && <Typography variant="body2" color="text.secondary">{officeContact ? (intake ? 'Office contact. Ask for the official assigned to your requirement.' : 'Office contact.') : 'Confirm their assignment to your requirement.'}</Typography>}
    </> : entry && <>
      {guide ? (intake || !entry.person_name) && <Typography variant="body2" color="text.secondary">
        {intake ? `${!entry.person_name ? 'Office contact. ' : ''}Ask for the person assigned to your requirement.` : 'Office contact; no named person listed.'}
      </Typography> : <>
        {intake && <Typography variant="body2" fontWeight={600}>Servicing office — help finding your assigned contact</Typography>}
        {!entry.person_name && <Typography variant="body2" color="text.secondary">This is an office contact, not a specific person.</Typography>}
      </>}
      {entry.person_name && !guideIdentity && <Typography fontWeight={600}>{entry.person_name}</Typography>}
      {entry.title && <Typography variant="body2">{entry.title}</Typography>}
      {entry.office_name && (!guideIdentity || entry.person_name) && <Typography variant="body2">{entry.office_name}</Typography>}
      {email && <Typography variant="body2">{entry.email_kind === 'office' && 'Office email: '}<Link href={`mailto:${encodeURIComponent(email)}`}>{email}</Link></Typography>}
      {entry.phones?.map(phone => <Typography variant="body2" key={`${phone.kind}/${phone.number}`}>{phone.kind === 'office' ? 'Office phone: ' : 'Phone: '}
        {/^[+\d\s().-]+$/.test(phone.number) ? <Link href={`tel:${phone.number.replace(/[^+\d]/g, '')}`}>{phone.number}</Link> : phone.number}</Typography>)}
      {page && <Link variant="body2" href={page} target="_blank" rel="noreferrer">Contact office page</Link>}
    </>}
    {!printOnly && <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
      {entry && <Button size="small" sx={{px:0}} aria-label={`Chat for ${raContactRoleLabels[roleId] || roleId}`} onClick={event=>launchChat(entry,roleId,reportContext,'chat',event.currentTarget)}><span aria-hidden="true">💬</span>{' Chat'}</Button>}
      {entry && !personalEmail(entry) && <Button size="small" sx={{px:0}} onClick={event=>launchChat(entry,roleId,reportContext,'add_email',event.currentTarget)}>Add email</Button>}
    <Button size="small" sx={{ alignSelf: 'flex-start', px: 0 }} onClick={() => report(roleId, reportContext)}>{entry ? 'Report incorrect contact' : 'Provide contact'}</Button>
    </Stack>}
  </Stack>;
}
